import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import BottomBar from './BottomBar';
import SiteFooter from './SiteFooter';
import { cn } from '@/lib/utils';

// Module pages (/courses/:courseId/:slug) enter focus mode — no sidebar/bottom bar
function isFocusMode(pathname: string) {
    const parts = pathname.split('/').filter(Boolean);
    return parts[0] === 'courses' && parts.length >= 3;
}

function isUtilityPage(pathname: string) {
    const parts = pathname.split('/').filter(Boolean);
    const root = parts[0];

    if (pathname === '/403' || pathname === '/privacy' || pathname === '/terms' || pathname === '/contact' || pathname === '/maintenance') {
        return true;
    }

    if (!root) return false;

    const knownRoots = new Set([
        'dashboard',
        'courses',
        'armory',
        'leaderboard',
        'profile',
        'settings',
        'auth',
        'game-guide',
        'privacy',
        'terms',
        'contact',
        'maintenance',
        '403',
        'base-camp',
        'grind',
        'hall-of-fame',
        'concepts',
        'knowledge-vault',
        'controls',
    ]);

    return !knownRoots.has(root);
}

interface AppLayoutProps {
    children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const focusMode = isFocusMode(pathname);
    const isLanding = pathname === '/';
    const utilityPage = isUtilityPage(pathname);

    // LandingPage remains a temporary standalone shell. Utility pages share top nav/footer but skip side rails.
    const showTopBar = !focusMode && !isLanding;
    const showSidebar = !!user && !focusMode && !isLanding && !utilityPage;
    const showBottomBar = !!user && !focusMode && !isLanding && !utilityPage;
    const showFooter = !focusMode && !isLanding && (!user || utilityPage);

    return (
        <div className="min-h-screen bg-game-bg">
            {showTopBar && <TopBar />}
            {showSidebar && <Sidebar />}

            <div className={cn(showSidebar && 'md:pl-64')}>
                <main className={cn(
                    showTopBar && 'pt-16',
                    showBottomBar && 'pb-16 md:pb-0',
                )}>
                    {children}
                </main>
                {showFooter && <SiteFooter />}
            </div>

            {showBottomBar && <BottomBar />}
        </div>
    );
}
